"use client";

import "./management-subscription-page.css";
import React, { useEffect, useRef, useState } from "react";
import Header from "../header/page";
import clsx from "clsx";
import dayjs from "dayjs";
import Skeleton from "react-loading-skeleton";
import CancelPopupSubscription from "./cancelPopupSubscription";
import { useManagementSubscriptionContext } from "./Context";
import CancelSubscription from "./cancelSubscription";
import MemberHero from "./components/MemberHero";
import Modal from "@/app/components/Modal/modal";
import { useAppDispatch, useAppSelector } from "@/app/redux/hooks";
import { publishModalVisibility } from "@/app/redux/features/modal/modalSlice";
import ChevronsRightIcon from "@/app/shared-components/icons/ChevronsRightIcon";
import PaymentProcessingModal from "../subscription/InvoiceProcessingModal";
import { OnCloseReason } from "../subscription/interfaces";
import { openInvoicePaymentModal } from "./openInvoicePaymentModal";

const PAST_DUE_STATUSES: string[] = ["open"];

const ManagementSubscription = () => {
  const ref = useRef<HTMLDivElement>();
  const dispatch = useAppDispatch();
  const isModalOpen = useAppSelector(state => state.modal.isOpen);
  const modalData = useAppSelector(state => state.modal.data);
  const modalOverlay = useAppSelector(state => state.modal.withOverlay);
  const modalClassNames = useAppSelector(state => state.modal.modalClassNames);
  const [minimumheight, setMinimumHeight] = useState<string>();
  const {
    isSubscriptionLoading,
    shouldShowCancelSubscriptionStep,
    subsWillBeCancelled,
    isInvoicesLoading,
    invoicesList,
    allPackages,
    setShouldShowCancelSubscriptionStep,
    isInvoiceProcessing,
    setIsInvoiceProcessing,
  } = useManagementSubscriptionContext();

  useEffect(() => {
    if (ref.current) {
      setMinimumHeight(`calc(100vh - ${ref.current.getBoundingClientRect().top}px)`);
    }
  }, [ref.current]);

  useEffect(() => {
    if (isSubscriptionLoading) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get("renew") === "1") {
      setShouldShowCancelSubscriptionStep(true);
    }
  }, [isSubscriptionLoading]);

  const capitalizeWord = (word: string) => {
    return word.charAt(0).toUpperCase() + word.slice(1);
  };

  const getInvoicePackageName = (invoice: Invoice): string => {
    const selectedPackage = allPackages.find(
      _package => _package.productId === invoice.lines.data[0]?.pricing?.price_details?.product
    );
    if (selectedPackage) {
      return selectedPackage.name;
    }
    return "";
  };

  const isPastDueInvoice = (invoice: Invoice) => PAST_DUE_STATUSES.includes(invoice.status);

  const onPayInvoice = (invoiceId: string) => {
    openInvoicePaymentModal(dispatch, invoiceId, {
      onClose: (reason?: OnCloseReason) => {
        if (reason === "successSubmit") {
          setIsInvoiceProcessing(true);
        }
      },
    });
  };

  const manageSubscription = () => (
    <div className="mainSection mx-auto flex px-0 lg:px-[1.5rem]">
      <div
        className="leftSection w-[30%] lg:shrink-0 relative mt-12 max-lg:hidden"
        style={{ minHeight: minimumheight }}
        ref={ref}>
        <h3 className="normal-case text-left text-lg lg:text-2xl font-semibold leading-[1.5rem] lg:leading-[1.875rem] !mb-0 text-dark-1">
          Manage your BOTB Pass
        </h3>
      </div>
      <div className="rightSection management-right-panel relative overflow-hidden lg:shadow-[-15px_4px_20px_-10px_rgba(0,0,0,0.10)] w-full lg:w-[70%] lg:shrink-0 pb-12">
        {isSubscriptionLoading ? (
          <div className="mt-6 lg:mt-12 px-7 lg:px-8">
            <Skeleton
              count={1}
              inline
              highlightColor="#fff"
              containerClassName="flex flex-col w-full h-[400px]"
              className="h-full"
            />
          </div>
        ) : (
          <>
            <div
              aria-hidden="true"
              className="absolute inset-x-0 top-0 h-[346px] lg:h-[331px] pointer-events-none bg-[linear-gradient(to_top,rgba(229,206,246,0)_0%,#e5cef6_100%)]"
            />
            <img
              src="/assets/images/management-subscription/pass-coin.webp"
              alt=""
              className="absolute -top-3.5 -right-7 lg:top-[-62px] lg:-right-6 size-[82px] lg:size-[205px] pointer-events-none select-none z-0 object-contain"
            />

            <div className="management-right-inner relative">
              <MemberHero />

              <div className="px-3 lg:px-8 mt-2 lg:mt-4">
                <div className="border border-black/8 p-3 lg:py-6 lg:px-8 rounded-lg">
                  <div className="text-xs font-normal leading-4 lg:text-sm text-dark-2/90 uppercase lg:font-medium lg:leading-5">
                    Invoice history
                  </div>
                  <div className="w-full h-px bg-black/8 my-3" />

                  {isInvoicesLoading ? (
                    <Skeleton
                      count={1}
                      inline
                      highlightColor="#fff"
                      containerClassName="block w-full h-[64px] lg:h-[40px]"
                      className="h-full"
                    />
                  ) : (
                    <div className="lg:max-w-[790px]">
                      <div className="grid grid-cols-[auto_auto_auto_auto_auto] items-center gap-x-2 lg:gap-x-16 gap-y-3 lg:gap-y-5 text-xs font-medium lg:font-normal leading-4 lg:text-base text-secondary">
                        {invoicesList.map(invoice => (
                          <React.Fragment key={invoice.id}>
                            <div className="whitespace-nowrap">
                              {dayjs(invoice.effective_at * 1000).format("DD MMM YYYY")}
                            </div>
                            <div className="whitespace-nowrap">£{invoice.amount_due / 100}</div>
                            <div className="whitespace-nowrap">
                              <span
                                className={clsx(
                                  "inline-flex items-center justify-center whitespace-nowrap text-[10px] font-medium leading-3 lg:text-[13px] lg:leading-4.5 rounded min-h-4 lg:min-h-7 px-1.5 lg:px-2 uppercase min-w-12 lg:min-w-16",
                                  {
                                    "text-[#256929] bg-[#C5EBC7] border border-[#A9E2AC] paid-status":
                                      invoice.status === "paid",
                                    "text-[#b3093c] bg-[#ffe7f2] border border-[#ffccdf] void-status":
                                      invoice.status === "void",
                                    "text-[#b3093c] bg-[#ffe7f2] border border-[#ffccdf] uncollectible-status":
                                      invoice.status === "uncollectible",
                                    "text-[#0051bb] bg-[#d0f5f7] border border-[#aee9f1] open-status":
                                      invoice.status === "open",
                                  }
                                )}>
                                {capitalizeWord(invoice.status)}
                              </span>
                            </div>
                            <div className="uppercase whitespace-nowrap truncate min-w-0">
                              {getInvoicePackageName(invoice)}
                            </div>
                            <div className="flex justify-end">
                              {isPastDueInvoice(invoice) && (
                                <button
                                  type="button"
                                  onClick={() => onPayInvoice(invoice.id)}
                                  className="theme-btn theme-btn-primary theme-btn-3xs lg:theme-btn-2sm uppercase whitespace-nowrap min-w-[49px] lg:min-w-[104px]">
                                  <span className="lg:hidden">Pay</span>
                                  <span className="hidden lg:inline">Pay now</span>
                                  <ChevronsRightIcon variant={2} className="hidden lg:block size-3 shrink-0" />
                                </button>
                              )}
                            </div>
                          </React.Fragment>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );

  const renderCancelSubscription = () => (
    <div className="mainSection mx-auto flex px-0 lg:px-[1.5rem]">
      <div className="leftSection w-[30%] relative mt-12 max-lg:hidden" style={{ minHeight: minimumheight }} ref={ref}>
        <h3 className="normal-case text-left text-lg lg:text-2xl font-semibold leading-[1.5rem] lg:leading-[1.875rem] !mb-0 text-dark-1">
          Manage your BOTB Pass
        </h3>
      </div>
      <div className="rightSection lg:shadow-[-15px_4px_20px_-10px_rgba(0,0,0,0.10)] lg:w-[70%] flex">
        <div className="w-full mt-6 mb-12 px-7 lg:mt-12 lg:ml-[1.875rem] lg:px-0">
          <CancelSubscription />
        </div>
      </div>
    </div>
  );

  return (
    <>
      <header className="sticky top-0 bg-white z-[1003] botb-pass-header">
        <Header renderModalComponentHere={false} isBotBPassHeader={true} />
      </header>
      <Modal
        isOpen={isModalOpen}
        title=""
        body={modalData}
        modalClassNames={modalClassNames}
        withOverlay={modalOverlay}
        closeModal={event => {
          event.stopPropagation();
          dispatch(publishModalVisibility({ isOpen: false }));
        }}
      />
      {isInvoiceProcessing && <PaymentProcessingModal />}
      {shouldShowCancelSubscriptionStep ? (
        subsWillBeCancelled ? (
          renderCancelSubscription()
        ) : (
          <>
            <CancelPopupSubscription />
            {manageSubscription()}
          </>
        )
      ) : (
        manageSubscription()
      )}
    </>
  );
};

export default ManagementSubscription;
